import { useNavigate, useLocation } from "react-router-dom";
import { LogOut } from "lucide-react";
import { z } from "zod";
import { useRoleStore } from "@/lib/stores/role";
import { useAuthStore } from "@/lib/stores/auth";
import { apiClient } from "@/lib/api/client";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { BranchFilter } from "./BranchFilter";

const TITLES: Record<string, string> = {
  dashboard: "首頁 Dashboard",
  schedule: "排課",
  coaches: "教練",
  students: "學員",
  reports: "報表",
};

function deriveTitle(pathname: string): string {
  const segs = pathname.split("/").filter(Boolean);
  // owner/dashboard | owner/coaches/:id | owner/reports/monthly
  if (segs.length >= 2) {
    const second = segs[1];
    return TITLES[second] ?? "";
  }
  return "";
}

export function Topbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentRole = useRoleStore((s) => s.currentRole);
  const logout = useRoleStore((s) => s.logout);
  const clearSession = useAuthStore((s) => s.clearSession);
  const authUser = useAuthStore((s) => s.user);

  const title = deriveTitle(location.pathname);

  const handleLogout = async () => {
    // #221: logout now goes through apiClient. The endpoint returns 204 No
    // Content, which apiClient tolerates — no more raw-fetch workaround. The
    // client attaches the Bearer token + base URL. Errors are ignored: we
    // always clear the local session regardless of the endpoint response.
    // TODO(wire-be): real BE invalidates token server-side on logout.
    try {
      await apiClient.post("/auth/logout", z.unknown(), undefined);
    } catch {
      // Ignore — always clear regardless of endpoint response
    }
    clearSession();
    logout();
    navigate("/login");
  };

  // Display role from auth user if available, otherwise fall back to role store
  const displayRole = authUser?.role ?? currentRole;

  return (
    <header className="border-border bg-surface flex h-12 shrink-0 items-center gap-3 border-b px-6">
      <div className="flex items-center gap-3">
        <span className="text-ink-900 text-sm font-semibold">{title}</span>
        <BranchFilter />
      </div>

      <div className="flex-1" />

      <Badge variant={displayRole === "owner" ? "primary" : "neutral"} appearance="subtle">
        {displayRole === "owner" ? "老闆" : displayRole === "trainer" ? "教練" : "館務經理"}
      </Badge>

      <Button
        variant="ghost"
        size="sm"
        onClick={handleLogout}
        leftIcon={<LogOut className="h-3.5 w-3.5" />}
        aria-label="登出"
      >
        登出
      </Button>
    </header>
  );
}
